import { useEffect, useRef, memo, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  fetchNotifications,
  dismissCriticalNotification,
} from 'services/misc/miscAsync';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  getCriticalNotifications,
  getCriticalNotificationsLoadingStatus,
  setCriticalNotificationsData,
} from 'services/misc/criticalNotificationSlice';
import Alert from 'components/Alert';
import LazySvg from 'components/LazySvg';
import If from 'components/If';
import { logErrors } from 'utils';
import {
  selectIsEligibleForDataCollection,
  selectIsDataCollectionBannerClosed,
} from 'services/misc/miscSelectors';
import DataCollectionBanner from 'pages/DataCollectionModal/banner';
import announceLayoutShift from 'utils/layoutShift';
import CustomNotification, {
  useCustomNotification,
} from './CustomNotification';
import styles from './index.module.scss';

// type
export type CriticalNotificationType = {
  id: string;
  message: string;
  notification: {
    /* BE flag — when true the notification is persisted on the
     * server and the client must call `PUT /notifications/{id}` to
     * dismiss it; when false the notification is transient and only
     * the local list needs updating. */
    userDeactivatable?: boolean;
    type?: string;
    severity?: string;
  };
};

function CriticalNotification() {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(getCriticalNotifications);
  const isNotificationsLoaded = useAppSelector(
    getCriticalNotificationsLoadingStatus,
  );
  const isEligibleForDataCollection = useAppSelector(
    selectIsEligibleForDataCollection,
  );
  const isDataCollectionBannerClosed = useAppSelector(
    selectIsDataCollectionBannerClosed,
  );
  /* fetch critical notifications.
   *
   * Ref guard dedupes `GET /api/client/notifications` across React
   * 18 StrictMode's mount → cleanup → mount cycle in dev. The
   * `!isNotificationsLoaded` slice-level gate is still correct,
   * but that flag only flips once the response lands in Redux —
   * so without the ref the second mount sees the same unloaded
   * state and fires a duplicate request. Same pattern as
   * `useSessionInitialSetup`'s user-details fetch. */
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    async function getNotifications() {
      try {
        await dispatch(fetchNotifications());
      } catch (error) {
        logErrors(error);
      }
    }

    if (isNotificationsLoaded) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    getNotifications();
  }, [isNotificationsLoaded, dispatch]);

  /* BE notifications only — Angular parity
   * (`critical-notifications.component.html:1-9`). The Discover
   * proprietary banner is delegated to the custom-notification tier
   * below, which stands down when this list is non-empty, so a BE
   * notification always wins over Discover CC. */
  const displayNotifications = notifications;

  /* Angular's custom-notification tier
   * (`critical-notifications.component.html:11-38`) — Discover CC
   * and Upsell. Both stand down when the slot is occupied by a BE
   * notification. */
  const { activeNotification, dismissCustomNotification } =
    useCustomNotification({
      isSlotOccupied: displayNotifications.length > 0,
    });

  /* This slot renders at the top of the content column, so whatever it shows
     moves every dashboard section below it. Both the PII banner and the
     notification list arrive from async fetches (and one can replace the other,
     changing the height again), all after the page has settled — so anything
     measuring the layout has to be told. See `utils/layoutShift`. Keyed on what
     is rendered rather than on the notification payloads, so it fires only when
     the height actually changes. */
  const visibleBanner = useMemo(() => {
    if (isEligibleForDataCollection && !isDataCollectionBannerClosed)
      return 'pii';
    /* The count is part of the key, not just the branch: the alerts stack, so
       dismissing one of several changes the height without changing which
       branch renders. */
    return displayNotifications.length
      ? `notifications:${displayNotifications.length}`
      : 'none';
  }, [
    isEligibleForDataCollection,
    isDataCollectionBannerClosed,
    displayNotifications.length,
  ]);

  useEffect(() => announceLayoutShift(), [visibleBanner]);

  /* The upsell sits outside `visibleBanner`'s accounting above, so it announces
     its own arrival and removal. `announceLayoutShift` is multi-caller by
     design — see `utils/layoutShift`. */
  useEffect(() => announceLayoutShift(), [activeNotification]);

  /* Angular parity — `critical-notifications.component.ts:183-190`.
   * Dismissing a notification takes one of two paths depending on
   * the BE's `userDeactivatable` flag:
   *   – userDeactivatable=true  → PUT /notifications/{id}, BE
   *     tombstones the row so it doesn't come back on the next
   *     fetch. We also remove it locally to give an instant response.
   *   – userDeactivatable=false → local removal only. These are
   *     things like the browser-unsupported banner that live only
   *     on the client and would come right back from the BE. */
  const handleNotificationClose = (item: CriticalNotificationType) => {
    if (item?.notification?.userDeactivatable && item?.id) {
      dispatch(dismissCriticalNotification(item.id)).catch(logErrors);
    }
    const updatedNotifications = notifications.filter((n) => n.id !== item.id);
    dispatch(setCriticalNotificationsData(updatedNotifications));
  };

  if (isEligibleForDataCollection && !isDataCollectionBannerClosed) {
    return (
      <div className={styles.bannerContainer}>
        <DataCollectionBanner />
      </div>
    );
  }

  /* Angular parity — `critical-notifications.component.html:3`
   * renders only `criticalNotifications[0]`, i.e. the single
   * highest-priority banner at a time. Rendering the full array
   * stacked them into the same grid cell (`.container` uses
   * `display: grid` with every `.notification` on `grid-row/column
   * -start: 1`) so the DOM-last banner painted on top of the sorted
   * first one — hiding the priority notification. Slicing to the
   * head puts the sort back in charge of what the user sees. */
  const visibleNotifications = displayNotifications.slice(0, 1);

  return (
    <>
      {/* The custom tier sits outside the grid above, which stacks every
          `.notification` in one cell. It renders null unless a custom banner is
          active, and the upsell only becomes active when that grid is empty —
          so the two are never on screen together. */}
      <CustomNotification
        name={activeNotification}
        onDismiss={dismissCustomNotification}
      />
      <If condition={!!visibleNotifications.length}>
        <div className={styles.container}>
          {visibleNotifications.map((item: CriticalNotificationType) => (
            <Alert
              key={item?.id}
              type='error'
              message={
                // eslint-disable-next-line react/no-danger
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item?.message ?? ''),
                  }}
                />
              }
              closable={{
                'aria-label': 'close',
                closeIcon: (
                  <LazySvg
                    name='close'
                    className={styles.closeIcon}
                    onClick={() => handleNotificationClose(item)}
                  />
                ),
              }}
              /* antd owns the close: it collapses and unmounts the alert
                 internally, so this component does not necessarily re-render and
                 the effect above cannot see it. `afterClose` is antd's own
                 post-collapse hook, which makes the announcement independent of
                 whether the Redux list was updated. */
              afterClose={() => {
                announceLayoutShift();
              }}
              className={styles.notification}
            />
          ))}
        </div>
      </If>
    </>
  );
}

export default memo(CriticalNotification);
